import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail as fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Save, Loader2, Trash2, AlertCircle, ShieldCheck, BookOpen, Download } from "lucide-react";
import { downloadRcaPdf } from "@/lib/rcaPdf";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RCA_SEVERITY, toLocalInput, fromLocalInput, duration, fmtDateTime } from "@/lib/rca";
import { SeverityBadge, RcaStatusBadge, Field, Section } from "@/components/rca/RcaBits";
import { TagInput, TimelineEditor, WhysEditor, CategorySelect, ActionsEditor, TextField } from "@/components/rca/RcaEditors";
import RcaStatusActions from "@/components/rca/RcaStatusActions";
import LinkedItems from "@/components/rca/LinkedItems";

const EDITABLE = ["title", "severity", "client_id", "engineer_id", "ticket_ids", "task_ids", "started_at", "detected_at",
  "resolved_at", "summary", "impact", "customers_affected", "affected_systems", "detection_method", "timeline", "whys",
  "root_cause", "root_cause_category", "contributing_factors", "resolution", "lessons_learned", "actions"];
const pick = (r) => Object.fromEntries(EDITABLE.map((k) => [k, r[k]]));

function PeopleSelect({ value, onChange, options, disabled, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
      <SelectContent>{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function TimeField({ label, value, onChange, disabled, testid }) {
  return (
    <Field label={label}>
      {disabled ? <p className="text-sm text-slate-700 pt-2">{fmtDateTime(value)}</p> : (
        <Input type="datetime-local" value={toLocalInput(value)} onChange={(e) => onChange(fromLocalInput(e.target.value))} data-testid={testid} />
      )}
    </Field>
  );
}

export default function RcaDetailPage() {
  const { rcaId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["manager", "admin"].includes(user?.role);
  const [rca, setRca] = useState(null);
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/rcas/${rcaId}`); setRca(data); setForm(pick(data)); }
    catch { toast.error("RCA not found"); navigate("/rca"); }
  }, [rcaId, navigate]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
    api.get("/tasks").then((r) => setTasks(r.data)).catch(() => {});
  }, []);

  const dirty = useMemo(() => rca && form && JSON.stringify(pick(rca)) !== JSON.stringify(form), [rca, form]);
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  if (!rca || !form) return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>;

  const ro = rca.status === "closed" || (rca.status === "approved" && !isManager);
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const clients = users.filter((u) => u.role === "client");
  const staff = users.filter((u) => u.role !== "client");
  const ticketOpts = tickets.filter((t) => !form.client_id || t.client_id === form.client_id).map((t) => ({ id: t.id, label: `${t.key} · ${t.title}` }));
  const taskOpts = tasks.map((t) => ({ id: t.id, label: t.title, board_id: t.board_id }));
  const linkedTickets = form.ticket_ids.map((id) => ticketOpts.find((t) => t.id === id) || tickets.filter((t) => t.id === id).map((t) => ({ id, label: `${t.key} · ${t.title}` }))[0] || { id, label: rca.tickets?.find((t) => t.id === id)?.key || "Ticket" });
  const linkedTasks = form.task_ids.map((id) => taskOpts.find((t) => t.id === id) || { id, label: rca.tasks?.find((t) => t.id === id)?.title || "Task", board_id: rca.tasks?.find((t) => t.id === id)?.board_id });

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch(`/rcas/${rca.id}`, form); setRca(data); setForm(pick(data)); toast.success("RCA saved"); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${rca.key}? This cannot be undone.`)) return;
    await api.delete(`/rcas/${rca.id}`); toast.success("RCA deleted"); navigate("/rca");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1000px] pb-28" data-testid="rca-detail-page">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <Link to="/rca" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="rca-back-link"><ArrowLeft className="w-4 h-4" />All RCAs</Link>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild data-testid="rca-read-report-button"><Link to={`/rca/${rca.id}/report`}><BookOpen className="w-3.5 h-3.5 mr-1.5" />Read report</Link></Button>
          <Button variant="outline" size="sm" disabled={dirty || exporting} title={dirty ? "Save your changes first" : ""} data-testid="rca-export-pdf-button"
            onClick={async () => { setExporting(true); await downloadRcaPdf(rca); setExporting(false); }}>
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}Export PDF
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="font-mono text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200" data-testid="rca-key">{rca.key}</span>
        <SeverityBadge sev={form.severity} /><RcaStatusBadge status={rca.status} />
        <span className="text-xs text-slate-400">by {rca.created_by_name} · {fmtDateTime(rca.created_at)}</span>
      </div>
      {ro ? <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 mb-4">{form.title}</h1> : (
        <Input value={form.title} onChange={(e) => set("title")(e.target.value)} className="font-display text-xl sm:text-2xl font-bold h-auto py-2 mb-4 border-transparent hover:border-slate-200 focus:border-indigo-300 px-2 -mx-2" data-testid="rca-title-edit" />
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap mb-6 bg-white border border-slate-200 rounded-xl p-3">
        <RcaStatusActions rca={rca} isManager={isManager} dirty={dirty} onChanged={load} />
        {rca.missing_fields?.length > 0 && rca.status === "draft" && (
          <p className="text-[11px] text-amber-700 inline-flex items-start gap-1 max-w-md" data-testid="rca-missing-fields"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />Needed before review: {rca.missing_fields.join(", ")}</p>
        )}
        {rca.approved_by && <p className="text-[11px] text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" />Approved by {rca.approved_by}</p>}
      </div>

      <div className="space-y-5">
        <Section n="1" title="Overview & ownership" desc="Who is affected and who owns this analysis." testid="rca-section-overview">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <Field label="Client"><PeopleSelect value={form.client_id} onChange={set("client_id")} disabled={ro} testid="rca-detail-client" options={clients.map((c) => [c.id, c.company || c.name])} /></Field>
            <Field label="Responsible engineer"><PeopleSelect value={form.engineer_id} onChange={set("engineer_id")} disabled={ro} testid="rca-detail-engineer" options={staff.map((u) => [u.id, u.name])} /></Field>
            <Field label="Severity"><PeopleSelect value={form.severity} onChange={set("severity")} disabled={ro} testid="rca-detail-severity" options={Object.entries(RCA_SEVERITY).map(([k, v]) => [k, v.label])} /></Field>
          </div>
          <TextField label="Executive summary" hint="2–3 sentences a non-technical reader can follow." value={form.summary} onChange={set("summary")} disabled={ro} testid="rca-summary-input" placeholder="What happened, who was affected, how it was fixed." />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Field label="Linked tickets"><LinkedItems label="ticket" testid="rca-linked-tickets" items={linkedTickets} options={ticketOpts} disabled={ro} hrefOf={() => "/tickets"} onAdd={(id) => set("ticket_ids")([...form.ticket_ids, id])} onRemove={(id) => set("ticket_ids")(form.ticket_ids.filter((x) => x !== id))} /></Field>
            <Field label="Linked tasks"><LinkedItems label="task" testid="rca-linked-tasks" items={linkedTasks} options={taskOpts} disabled={ro} hrefOf={(t) => (t.board_id ? `/boards/${t.board_id}` : "/dashboard")} onAdd={(id) => set("task_ids")([...form.task_ids, id])} onRemove={(id) => set("task_ids")(form.task_ids.filter((x) => x !== id))} /></Field>
          </div>
        </Section>

        <Section n="2" title="Incident window" desc="Key timestamps — time to detect (TTD) and time to resolve (TTR) are calculated automatically." testid="rca-section-window">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <TimeField label="Incident started" value={form.started_at} onChange={set("started_at")} disabled={ro} testid="rca-started-input" />
            <TimeField label="Detected" value={form.detected_at} onChange={set("detected_at")} disabled={ro} testid="rca-detected-input" />
            <TimeField label="Resolved" value={form.resolved_at} onChange={set("resolved_at")} disabled={ro} testid="rca-resolved-input" />
          </div>
          <div className="flex gap-3 mt-4 flex-wrap">
            {[["Time to detect", form.started_at, form.detected_at, "ttd"], ["Time to resolve", form.detected_at, form.resolved_at, "ttr"], ["Total duration", form.started_at, form.resolved_at, "total"]].map(([l, a, b, id]) => (
              <div key={id} className="rounded-lg bg-slate-50 px-3 py-2"><p className="text-base font-display font-bold text-slate-900" data-testid={`rca-metric-${id}`}>{duration(a, b)}</p><p className="text-[11px] text-slate-500">{l}</p></div>
            ))}
          </div>
          <div className="mt-4"><TextField label="How was it detected?" value={form.detection_method} onChange={set("detection_method")} disabled={ro} testid="rca-detection-input" placeholder="e.g. Client reported via ticket / monitoring alert" rows="min-h-[44px]" /></div>
        </Section>

        <Section n="3" title="Impact" desc="Quantify the effect on the client and their users." testid="rca-section-impact">
          <TextField label="Impact description" value={form.impact} onChange={set("impact")} disabled={ro} testid="rca-impact-input" placeholder="e.g. 18% of checkout requests failed for 42 minutes" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <TextField label="Users / customers affected" value={form.customers_affected} onChange={set("customers_affected")} disabled={ro} testid="rca-customers-input" placeholder="e.g. ~1,200 end users" rows="min-h-[44px]" />
            <Field label="Affected systems"><TagInput value={form.affected_systems} onChange={set("affected_systems")} disabled={ro} placeholder="Type and press Enter" testid="rca-systems" /></Field>
          </div>
        </Section>

        <Section n="4" title="Timeline" desc="Chronological, factual events — detection, escalation, mitigation, resolution." testid="rca-section-timeline">
          <TimelineEditor value={form.timeline} onChange={set("timeline")} disabled={ro} />
        </Section>

        <Section n="5" title="Root cause analysis" desc="Use the 5 Whys to move past symptoms. Focus on systems and processes, not people." testid="rca-section-rootcause">
          <WhysEditor value={form.whys} onChange={set("whys")} disabled={ro} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
            <Field label="Root cause category"><CategorySelect value={form.root_cause_category} onChange={set("root_cause_category")} disabled={ro} /></Field>
            <div className="sm:col-span-2"><TextField label="Root cause statement" value={form.root_cause} onChange={set("root_cause")} disabled={ro} testid="rca-rootcause-input" placeholder="The underlying cause that, if fixed, prevents recurrence." /></div>
          </div>
          <div className="mt-4"><TextField label="Contributing factors" value={form.contributing_factors} onChange={set("contributing_factors")} disabled={ro} testid="rca-factors-input" placeholder="Conditions that made it worse or delayed detection." /></div>
        </Section>

        <Section n="6" title="Resolution" desc="What was done to restore service." testid="rca-section-resolution">
          <TextField label="Resolution & recovery" value={form.resolution} onChange={set("resolution")} disabled={ro} testid="rca-resolution-input" placeholder="e.g. Rolled back release 2.4.1 and replayed failed jobs" />
        </Section>

        <Section n="7" title="Corrective & preventive actions (CAPA)" desc="Each action needs an owner and due date. The RCA can only close when all are done." testid="rca-section-actions">
          <ActionsEditor value={form.actions} onChange={set("actions")} staff={staff} disabled={ro} />
        </Section>

        <Section n="8" title="Lessons learned" desc="What went well, what didn't, and where we got lucky." testid="rca-section-lessons">
          <TextField label="Lessons learned" value={form.lessons_learned} onChange={set("lessons_learned")} disabled={ro} testid="rca-lessons-input" />
        </Section>

        <Section n="9" title="History" testid="rca-section-history">
          <ol className="space-y-2">
            {(rca.history || []).slice().reverse().map((h, i) => (
              <li key={i} className="text-xs text-slate-600 flex gap-2"><RcaStatusBadge status={h.status} /><span>{h.by} · {fmtDateTime(h.at)}{h.note ? ` — “${h.note}”` : ""}</span></li>
            ))}
          </ol>
          {isManager && <Button variant="ghost" className="mt-4 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={remove} data-testid="rca-delete-button"><Trash2 className="w-4 h-4 mr-1.5" />Delete RCA</Button>}
        </Section>
      </div>

      {!ro && (
        <div className={`fixed z-20 right-4 sm:right-8 bottom-[calc(5rem+env(safe-area-inset-bottom))] md:bottom-6 transition-[opacity,transform] ${dirty ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 shadow-lg h-11 px-5 rounded-full" data-testid="rca-save-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}Save changes
          </Button>
        </div>
      )}
    </div>
  );
}
